import React from 'react';
import { Box, Button, Flex, HStack, Heading, Link, Skeleton, SkeletonText, Stack, Text, VStack } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { LuInfo } from 'react-icons/lu';
import {
  formatCurrency,
  formatDateLocal,
  getMyActivity,
  logger,
  type DonationEvent,
  type DonationPaymentMethod,
  type MonthlyDonationProfile,
} from '@kuow/core';
import { useAuth } from '@kuow/shared-ui';
import { CancelDonationDialog } from '../components/CancelDonationDialog';
import { ActivityTimelineItem } from '../components/donation-history/ActivityTimelineItem';
import { ReceiptModal } from '../components/donation-history/ReceiptModal';
import { TIMELINE_EVENT_TYPES, isPaymentEvent } from '../components/donation-history/activityFormat';

const SUPPORT_PHONE = '(206) 221-2501';
const SUPPORT_PHONE_HREF = 'tel:+12062212501';
const PAGE_SIZE = 4;
const SESSION_BASELINE_KEY_PREFIX = 'kuow:profile:session_baseline';

function getSessionBaseline(userId: string | number | undefined): number | null {
  if (userId === undefined || typeof window === 'undefined') return null;
  const key = `${SESSION_BASELINE_KEY_PREFIX}:${userId}`;
  const existing = window.sessionStorage.getItem(key);
  if (existing !== null) {
    const parsed = Number(existing);
    if (Number.isFinite(parsed)) return parsed;
  }
  const now = Date.now();
  window.sessionStorage.setItem(key, String(now));
  return now;
}

type PaymentsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; items: DonationEvent[]; total: number; loadMoreError?: string }
  | { status: 'error'; message: string };

export const MembersProfilePage: React.FC = () => {
  const { user, member, refreshMember } = useAuth();
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [receiptEvent, setReceiptEvent] = React.useState<DonationEvent | null>(null);

  const refreshedOnMountRef = React.useRef(false);
  React.useEffect(() => {
    if (refreshedOnMountRef.current) return;
    refreshedOnMountRef.current = true;
    void refreshMember();
  }, [refreshMember]);

  const trimmedFirstName = user?.first_name?.trim();
  const greetingName =
    trimmedFirstName !== undefined && trimmedFirstName.length > 0
      ? trimmedFirstName
      : (user?.email ?? 'member');
  const memberName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || greetingName;

  const monthly = member?.monthly_donation ?? null;
  const hasMonthly = Boolean(monthly && monthly.status !== 'canceled');

  const [perPage, setPerPage] = React.useState(PAGE_SIZE);
  const [payments, setPayments] = React.useState<PaymentsState>({ status: 'idle' });

  const sessionBaselineMs = React.useMemo(
    () => getSessionBaseline(user?.id),
    [user?.id],
  );

  const donatedThisSession = React.useMemo(() => {
    if (sessionBaselineMs === null) return false;
    if (payments.status !== 'loaded') return false;
    return payments.items.some((p) => {
      if (!isPaymentEvent(p)) return false;
      const t = Date.parse(p.occurred_at);
      return Number.isFinite(t) && t >= sessionBaselineMs;
    });
  }, [payments, sessionBaselineMs]);

  React.useEffect(() => {
    const controller = new AbortController();
    setPayments((prev) => (prev.status === 'loaded' ? prev : { status: 'loading' }));

    void (async () => {
      try {
        const result = await getMyActivity(
          { page: 1, per_page: perPage, event_types: [...TIMELINE_EVENT_TYPES] },
          { signal: controller.signal },
        );
        setPayments({ status: 'loaded', items: result.data, total: result.total });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        logger.error('Failed to load member payments', { error });
        const message = error instanceof Error ? error.message : 'Unable to load donation history.';
        setPayments((prev) => {
          if (prev.status === 'loaded') {
            return { ...prev, loadMoreError: message };
          }
          return { status: 'error', message };
        });
      }
    })();

    return () => controller.abort();
  }, [perPage]);

  return (
    <>
      <Helmet>
        <title>My Profile | KUOW</title>
      </Helmet>
      <Box w="full" bg="bg" flex="1" py={{ base: 6, md: 12 }}>
        <Box maxW="7xl" mx="auto" px={{ base: 4, md: 6 }}>
          <Stack direction={{ base: 'column', md: 'row' }} align="stretch" gap={{ base: 8, md: 12 }}>
            <Box flex="1" minW={0}>
              {hasMonthly && monthly ? (
                <ActiveSupporterIntro
                  firstName={greetingName}
                  monthly={monthly}
                  onCancelClick={() => setCancelOpen(true)}
                  hideUpgradePitch={donatedThisSession}
                />
              ) : (
                <EvergreenUpsell firstName={greetingName} />
              )}
            </Box>
            <Box
              w={{ base: 'full', md: 'clamp(420px, 33vw, 500px)' }}
              minW={{ md: '420px' }}
              maxW={{ md: '500px' }}
              flexShrink={0}
              bg="bg.subtle"
              borderRadius="l3"
              px={{ base: 6, md: 10 }}
              py={{ base: 8, md: 12 }}
            >
              <DonationHistory
                state={payments}
                perPage={perPage}
                onLoadMore={() => setPerPage((n) => n + PAGE_SIZE)}
                onViewReceipt={setReceiptEvent}
              />
            </Box>
          </Stack>
        </Box>
      </Box>
      <CancelDonationDialog
        open={cancelOpen}
        donationId={monthly?.id ?? null}
        onOpenChange={setCancelOpen}
        onCancelled={() => refreshMember()}
      />
      <ReceiptModal
        event={receiptEvent}
        open={receiptEvent !== null}
        memberName={memberName}
        onOpenChange={(open) => { if (!open) setReceiptEvent(null); }}
      />
    </>
  );
};

interface ActiveSupporterIntroProps {
  firstName: string;
  monthly: MonthlyDonationProfile;
  onCancelClick: () => void;
  hideUpgradePitch: boolean;
}

const ActiveSupporterIntro: React.FC<ActiveSupporterIntroProps> = ({ firstName, monthly, onCancelClick, hideUpgradePitch }) => {
  const currentAmount = parseMonthlyAmount(monthly.amount);
  const upgradeAmount = currentAmount !== null ? Math.round(currentAmount + 5) : null;
  const monthlyAmountLabel = formatCurrency(monthly.amount, true);
  const upgradeAmountLabel = upgradeAmount !== null ? formatCurrency(upgradeAmount, true) : null;

  const upgradeHref = upgradeAmount !== null
    ? `/donate?amount=${upgradeAmount}&frequency=monthly`
    : null;
  const updateHref = currentAmount !== null
    ? `/donate?amount=${currentAmount}&frequency=monthly`
    : null;

  return (
    <VStack align="stretch" gap={{ base: 6, md: 8 }}>
      <Heading as="h1" size={{ base: '2xl', md: '4xl' }} fontWeight="semibold" lineHeight="1.2">
        Thank you {firstName} for your ongoing support of KUOW!
      </Heading>
      <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.6">
        Each month your generous donation of <Text as="span" fontWeight="bold">{monthlyAmountLabel}</Text>
        {' '}helps provide steady support for the station.
        {!hideUpgradePitch ? (
          <>
            {' '}
            {upgradeAmountLabel ? (
              <Text as="span" fontStyle="italic">
                If you possibly can upgrade your donation to <Text as="span" fontWeight="bold">{upgradeAmountLabel}</Text> a
                month you would help us continue to produce quality, independent journalism well into the future!
              </Text>
            ) : (
              <Text as="span" fontStyle="italic">
                If you possibly can upgrade your donation, you would help us continue to produce quality, independent
                journalism well into the future!
              </Text>
            )}{' '}
            {upgradeHref ? (
              <Link asChild color="river.solid" textDecoration="underline">
                <RouterLink to={upgradeHref}>Upgrade my donation »</RouterLink>
              </Link>
            ) : null}
          </>
        ) : null}
      </Text>
      <EvergreenDonationCard monthly={monthly} updateHref={updateHref} onCancelClick={onCancelClick} />
    </VStack>
  );
};

function parseMonthlyAmount(value: number | string | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

interface EvergreenUpsellProps {
  firstName: string;
}

const EvergreenUpsell: React.FC<EvergreenUpsellProps> = ({ firstName }) => {
  return (
    <VStack align="stretch" gap={{ base: 6, md: 8 }}>
      <Heading as="h1" size={{ base: '2xl', md: '4xl' }} fontWeight="semibold" lineHeight="1.2">
        Welcome, {firstName}
      </Heading>
      <Heading as="h2" size={{ base: 'lg', md: 'xl' }} fontWeight="semibold" lineHeight="1.3">
        You can make an even bigger impact as an Evergreen Member!
      </Heading>
      <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.6">
        Monthly Evergreen donations provide steady support for the station. Help KUOW continue to produce
        quality, independent journalism into the future!
      </Text>
      <Box>
        <Button asChild colorPalette="river" size="lg">
          <RouterLink to="/donate?frequency=monthly">Yes, I&rsquo;d like to be an Evergreen Member</RouterLink>
        </Button>
      </Box>
    </VStack>
  );
};

interface EvergreenDonationCardProps {
  monthly: MonthlyDonationProfile;
  updateHref: string | null;
  onCancelClick: () => void;
}

const EvergreenDonationCard: React.FC<EvergreenDonationCardProps> = ({ monthly, updateHref, onCancelClick }) => {
  const amount = formatCurrency(monthly.amount, true);
  const nextBillDate = formatDateLocal(monthly.next_bill_date);
  const paymentMethodLabel = describePaymentMethod(monthly.payment_method ?? null);

  return (
    <Box
      bg="bg.panel"
      borderRadius="l3"
      px={{ base: 5, md: 5 }}
      py={5}
      boxShadow="sm"
      borderWidth="1px"
      borderColor="bg.muted"
    >
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        align={{ base: 'flex-start', sm: 'center' }}
        justify="space-between"
        gap={3}
        mb={6}
      >
        <Heading as="h2" size="lg" fontWeight="semibold">
          Your Evergreen Donation
        </Heading>
        <HStack gap={2}>
          {updateHref ? (
            <Button asChild colorPalette="river" size="sm">
              <RouterLink to={updateHref}>Update</RouterLink>
            </Button>
          ) : (
            <Button colorPalette="river" size="sm" disabled aria-disabled>
              Update
            </Button>
          )}
          <Button variant="ghost" size="sm" color="gray.fg" onClick={onCancelClick}>
            Cancel
          </Button>
        </HStack>
      </Flex>

      <Stack
        direction={{ base: 'column', sm: 'row' }}
        gap={{ base: 4, sm: 5 }}
        align="flex-start"
        wrap="wrap"
      >
        <DonationStat label="Monthly Donation Amount">
          <Text fontSize="xl" fontWeight="semibold">
            {amount} / month
          </Text>
        </DonationStat>
        <DonationStat label="Next Donation Scheduled For">
          <Text fontSize="xl" fontWeight="semibold">
            {nextBillDate}
          </Text>
        </DonationStat>
        <DonationStat label="Payment Method">
          <Text fontSize="xl" fontWeight="semibold" lineHeight="1.4">
            {paymentMethodLabel.primary}
          </Text>
          {paymentMethodLabel.secondary !== null ? (
            <Text fontSize="lg" fontWeight="semibold" lineHeight="1.4">
              {paymentMethodLabel.secondary}
            </Text>
          ) : null}
        </DonationStat>
      </Stack>

      <HStack gap={2} mt={6} align="flex-start">
        <Box color="fg.muted" mt="1px">
          <LuInfo size={16} aria-hidden />
        </Box>
        <Text fontSize="xs" color="fg.muted" lineHeight="1.4">
          Please note that your financial institution may require up to 7 days to process your automatic
          withdrawal.
        </Text>
      </HStack>
    </Box>
  );
};

interface DonationStatProps {
  label: string;
  children: React.ReactNode;
}

const DonationStat: React.FC<DonationStatProps> = ({ label, children }) => (
  <VStack flex="1" minW="160px" align="flex-start" gap={2}>
    <Text fontSize="sm" fontStyle="italic" fontWeight="semibold" color="gray.600">
      {label}
    </Text>
    <Box>{children}</Box>
  </VStack>
);

interface DonationHistoryProps {
  state: PaymentsState;
  perPage: number;
  onLoadMore: () => void;
  onViewReceipt: (event: DonationEvent) => void;
}

const DonationHistory: React.FC<DonationHistoryProps> = ({ state, perPage, onLoadMore, onViewReceipt }) => {
  const items = state.status === 'loaded' ? state.items : [];
  const hasMore = state.status === 'loaded' && state.total > state.items.length;

  return (
    <VStack align="stretch" gap={6}>
      <VStack align="stretch" gap={1}>
        <Heading as="h2" size="md" fontWeight="bold">Donation History</Heading>
        <Text fontSize="sm" lineHeight="1.4">
          Do you have questions about your previous donations? Please call us at{' '}
          <Link href={SUPPORT_PHONE_HREF} fontWeight="semibold" color="inherit">{SUPPORT_PHONE}</Link>.
        </Text>
      </VStack>

      {state.status === 'loading' || state.status === 'idle' ? (
        <VStack align="stretch" gap={4} aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Box key={i}><Skeleton height="20px" width="60%" mb={2} /><SkeletonText noOfLines={1} /></Box>
          ))}
        </VStack>
      ) : state.status === 'error' ? (
        <Text fontSize="sm" color="fg.error">{state.message}</Text>
      ) : items.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">No donation history yet.</Text>
      ) : (
        <>
          <Box>
            {items.map((event, i) => (
              <ActivityTimelineItem
                key={event.id}
                event={event}
                isLast={i === items.length - 1}
                onViewReceipt={onViewReceipt}
              />
            ))}
          </Box>
          {state.status === 'loaded' && state.loadMoreError ? (
            <Text fontSize="sm" color="fg.error">{state.loadMoreError}</Text>
          ) : null}
          {hasMore ? (
            <Box>
              <Button
                variant="outline"
                size="sm"
                onClick={onLoadMore}
                loading={state.status === 'loaded' && state.items.length < perPage && !state.loadMoreError}
              >
                Load More
              </Button>
            </Box>
          ) : null}
        </>
      )}
    </VStack>
  );
};

function describePaymentMethod(method: DonationPaymentMethod | null): { primary: string; secondary: string | null } {
  if (!method) return { primary: 'Payment on file', secondary: null };
  if (method.account_type === 'credit_card' && method.credit_card) {
    const brand = (method.credit_card.brand ?? '').toUpperCase();
    const number = method.credit_card.number ?? '';
    const lastDigits = number.replace(/\D/g, '').slice(-4);
    return {
      primary: 'Credit Card',
      secondary: lastDigits ? `${brand || 'CARD'} *${lastDigits}` : (brand || null),
    };
  }
  if (method.account_type === 'bank_account' && method.bank_account) {
    const bank = method.bank_account.bank_name ?? 'Bank Account';
    const acct = method.bank_account.account_number ?? '';
    const lastDigits = acct.replace(/\D/g, '').slice(-4);
    return { primary: bank, secondary: lastDigits ? `*${lastDigits}` : null };
  }
  if (method.account_type === 'paypal') return { primary: 'PayPal', secondary: null };
  if (method.account_type === 'apple_pay') return { primary: 'Apple Pay', secondary: null };
  return { primary: 'Payment on file', secondary: null };
}

